import type { ToolConfig } from "../types";
import { createPercentageTool } from "../factories";

export const taxTools: ToolConfig[] = [
  // 1. Income Tax Calculator (New Regime 2025-26)
  createPercentageTool({
    slug: "income-tax-calculator",
    category: "tax",
    title: "Income Tax Calculator (New Regime 2025-26)",
    shortTitle: "Income Tax",
    description: "Calculate your income tax liability under 2025-26 new regime",
    keywords: ["income tax", "new regime", "tax calculator"],
    inputs: [
      {
        type: "number",
        key: "grossIncome",
        label: "Gross Annual Income",
        prefix: "₹",
        min: 0,
        max: 50000000,
        step: 100000,
        default: 1500000,
      },
      {
        type: "number",
        key: "deductions",
        label: "Deductions (if any)",
        prefix: "₹",
        min: 0,
        max: 10000000,
        step: 50000,
        default: 0,
        helpText: "Leave empty if using standard deduction",
      }
    ],
    calculate(v) {
      const income = v.grossIncome as number;
      let deductions = v.deductions as number;

      // New Regime 2025-26: Standard deduction ₹75,000
      if (deductions === 0) {
        deductions = 75000;
      }

      const taxableIncome = Math.max(0, income - deductions);

      // New Regime slabs 2025-26
      let tax = 0;
      if (taxableIncome <= 400000) {
        tax = 0;
      } else if (taxableIncome <= 800000) {
        tax = (taxableIncome - 400000) * 0.05;
      } else if (taxableIncome <= 1200000) {
        tax = 20000 + (taxableIncome - 800000) * 0.1;
      } else if (taxableIncome <= 1600000) {
        tax = 60000 + (taxableIncome - 1200000) * 0.15;
      } else if (taxableIncome <= 2000000) {
        tax = 120000 + (taxableIncome - 1600000) * 0.2;
      } else if (taxableIncome <= 2400000) {
        tax = 200000 + (taxableIncome - 2000000) * 0.25;
      } else {
        tax = 300000 + (taxableIncome - 2400000) * 0.3;
      }

      // Health and Education Cess: 4% on income tax
      const cess = tax * 0.04;
      const totalTax = tax + cess;

      return {
        grossIncome: income,
        deductions: Math.round(deductions),
        taxableIncome: Math.round(taxableIncome),
        incomeTax: Math.round(tax),
        cess: Math.round(cess),
        totalTax: Math.round(totalTax),
        effectiveRate: taxableIncome > 0 ? Math.round((totalTax / taxableIncome) * 100 * 100) / 100 : 0,
      };
    },
    resultFields: [
      { key: "taxableIncome", label: "Taxable Income", format: "currency" },
      { key: "incomeTax", label: "Income Tax", format: "currency" },
      { key: "cess", label: "Health & Education Cess", format: "currency" },
      { key: "totalTax", label: "Total Tax", format: "currency", isPrimary: true },
      { key: "effectiveRate", label: "Effective Tax Rate", format: "percent" },
    ],
    chartSegments: [
      { label: "Net Income", valueKey: "grossIncome", color: "#10b981" },
      { label: "Income Tax", valueKey: "totalTax", color: "#ef4444" },
    ],
    content: {
      whatIs:
        "The new income tax regime introduced in 2023-24 provides significant tax relief through lower tax slabs, making it ideal for most salaried employees and professionals. The regime offers a standard deduction of 75000 rupees and progressive tax rates starting from just 5% on income above 400000 rupees. Unlike the old regime which allows multiple deductions under sections like 80C (investments), 80D (health insurance), and 80E (education loans), the new regime simplifies taxation by limiting deductions. This calculator helps you determine your exact tax liability including health and education cess (4% surcharge on income tax), which is mandatory for all taxpayers. The new regime is particularly beneficial if you have minimal deductible expenses, receive HRA, or have no major life insurance or investment plans. Union Budget 2024-25 further popularized this regime with improved rates, making it the default choice for most individuals unless you have substantial Section 80 deductions exceeding 200000 rupees.",
      sections: [
        {
          title: "Who Should Use This Calculator?",
          body: "This calculator is essential for salaried employees earning above 500000 rupees annually, self-employed professionals, gig workers, and anyone subject to income tax under the new regime. It helps individuals planning to opt for the new regime instead of old regime, students crossing the taxable income threshold, and those receiving income from multiple sources including salary, business, capital gains, and interest. If you earn below 500000 rupees, no tax is due under the new regime's 400000 rupee exemption limit, but knowing your exact liability helps with financial planning."
        },
        {
          title: "How Does This Calculator Work?",
          body: "The calculator first applies the standard deduction of 75000 rupees to your gross annual income, reducing it to the taxable income base. It then applies the 2025-26 tax slab rates progressively: 0% up to 400000 rupees, 5% from 400000-800000 rupees, 10% from 800000-1200000 rupees, increasing to 30% for income above 2400000 rupees. After calculating the base income tax, the calculator adds Health and Education Cess at 4% of the computed tax, giving you the total tax liability. This method ensures you understand each component of your tax burden separately."
        },
        {
          title: "Key Rules & Tax Regulations",
          body: "Section 115BAC of the Income Tax Act 1961 governs the new regime, offering optional lower tax rates without most deductions. The standard deduction of 75000 rupees is available without any documentation or conditions. The Health and Education Cess is levied under Section 4A of the Finance Act 2007 at 4% of income tax. While the new regime has become the default from FY 2023-24, the old regime remains available as an option if deductions exceed 200000 rupees. Recent Finance Acts and Union Budget announcements have continuously refined these thresholds to provide greater tax relief to middle-income individuals."
        },
        {
          title: "Benefits of Using This Calculator",
          body: "This calculator provides instant clarity on your tax liability without guesswork, helping you plan your finances accurately. It shows the impact of different income levels on your take-home salary, assists in investment decisions, and helps you understand effective tax rates. By comparing this with old regime calculations, you can make informed regime choices annually. The calculator also helps employers calculate employee tax liabilities for payroll purposes and assists financial planners in client advisory."
        },
        {
          title: "Tax Saving Opportunities",
          body: "Even under the new regime with limited deductions, you can optimize tax through investment in specified instruments that reduce taxable income. Consider strategic investments in tax-saving fixed deposits, tax-free bonds, and understanding HRA exemptions if applicable. Filing ITR on time ensures you don't face penalties or loss of exemptions. Regular income tax filing also builds credit history beneficial for loans and financial credibility."
        }
      ],
      faqs: [
        {
          question: "What is the new vs old income tax regime?",
          answer:
            "New regime: Lower tax rates but limited deductions (only standard deduction ₹75K). Old regime: Higher rates but allows deductions (80C, 80D, 80E, etc up to ₹3L+). Most salaried employees benefit from new regime.",
        },
        {
          question: "When should I use the old regime?",
          answer:
            "If you have substantial deductions under sections 80C (₹1.5L), 80D (₹1-2L), 80E (education loan), 80G (donations). If total deductions exceed ₹2L, old regime may be beneficial. Compare both to decide.",
        },
        {
          question: "Can I switch between regimes?",
          answer:
            "Yes, you can choose the regime each financial year. Once you opt for old regime, you must continue (only option to switch back is in specific cases). Default is new regime from 2023-24 onwards.",
        },
        {
          question: "What is standard deduction?",
          answer:
            "Standard deduction of ₹75,000 is automatically available in new regime without any documentation. In old regime, no standard deduction; you claim actual deductions.",
        },
        {
          question: "Are HRA and other allowances taxable?",
          answer:
            "HRA is taxable but exempt to the extent of (a) HRA received, (b) 50% of salary (metro) or 40% (non-metro), or (c) Actual rent minus 10% of salary. Whichever is least is taxable.",
        }
      ],
      relatedSlugs: [
        "tax/income-tax-old-regime",
        "tax/old-vs-new-regime",
        "tax/hra-exemption-calculator",
      ],
    },
    }),

  // 2. Income Tax Calculator (Old Regime)
  createPercentageTool({
    slug: "income-tax-old-regime",
    category: "tax",
    title: "Income Tax Calculator (Old Regime)",
    shortTitle: "Income Tax (Old Regime)",
    description: "Maximize tax savings using Section 80 deductions and investment strategies",
    keywords: ["old regime", "income tax", "deductions"],
    inputs: [
      {
        type: "number",
        key: "grossIncome",
        label: "Gross Annual Income",
        prefix: "₹",
        min: 0,
        max: 50000000,
        step: 100000,
        default: 1500000,
      },
      {
        type: "number",
        key: "section80C",
        label: "Section 80C Deduction (up to ₹1.5L)",
        prefix: "₹",
        min: 0,
        max: 150000,
        step: 10000,
        default: 150000,
        helpText: "PPF, ELSS, Life Insurance, FD, etc.",
      },
      {
        type: "number",
        key: "section80D",
        label: "Section 80D (Health Insurance)",
        prefix: "₹",
        min: 0,
        max: 300000,
        step: 10000,
        default: 50000,
        helpText: "Self + family ₹1.5L, Parents ₹1L",
      },
      {
        type: "number",
        key: "section80E",
        label: "Section 80E (Education Loan Interest)",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 0,
        helpText: "No limit on deduction",
      },
      {
        type: "number",
        key: "section80G",
        label: "Section 80G (Donations)",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 0,
        helpText: "50% or 100% deduction depending on charity",
      }
    ],
    calculate(v) {
      const income = v.grossIncome as number;
      const s80C = v.section80C as number;
      const s80D = v.section80D as number;
      const s80E = v.section80E as number;
      const s80G = v.section80G as number;

      const totalDeductions = s80C + s80D + s80E + s80G;
      const taxableIncome = Math.max(0, income - totalDeductions);

      // Old Regime slabs
      let tax = 0;
      if (taxableIncome <= 250000) {
        tax = 0;
      } else if (taxableIncome <= 500000) {
        tax = (taxableIncome - 250000) * 0.05;
      } else if (taxableIncome <= 1000000) {
        tax = 12500 + (taxableIncome - 500000) * 0.2;
      } else {
        tax = 112500 + (taxableIncome - 1000000) * 0.3;
      }

      // Cess: 4% on tax
      const cess = tax * 0.04;
      const totalTax = tax + cess;

      return {
        grossIncome: income,
        totalDeductions: totalDeductions,
        taxableIncome: Math.round(taxableIncome),
        incomeTax: Math.round(tax),
        cess: Math.round(cess),
        totalTax: Math.round(totalTax),
        effectiveRate:
          taxableIncome > 0
            ? Math.round((totalTax / taxableIncome) * 100 * 100) / 100
            : 0,
      };
    },
    resultFields: [
      { key: "totalDeductions", label: "Total Deductions", format: "currency" },
      { key: "taxableIncome", label: "Taxable Income", format: "currency" },
      { key: "incomeTax", label: "Income Tax", format: "currency" },
      { key: "cess", label: "Cess", format: "currency" },
      { key: "totalTax", label: "Total Tax", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs:
        "The old income tax regime, available as an optional choice from FY 2023-24 onwards, remains highly beneficial for individuals with substantial deductible expenses and investments. This regime allows multiple deductions under various Income Tax Act sections, including Section 80C (investments up to 150000 rupees), Section 80D (health insurance up to 200000 rupees), Section 80E (education loan interest unlimited), and Section 80G (charitable donations 50-100%). The old regime's higher tax rates are more than offset when total deductions exceed 200000 rupees annually. Salaried employees with life insurance policies, PPF/ELSS investments, health insurance premiums, or education loan obligations should calculate their tax under both regimes to determine savings. This calculator automatically processes all eligible deductions and applies the old regime's graduated tax slabs, showing you whether staying in the old regime offers genuine savings compared to the new regime's simpler but restrictive structure.",
      sections: [
        {
          title: "Who Should Use This Calculator?",
          body: "The old regime calculator is indispensable for individuals with substantial Section 80 deductions, including those with active life insurance policies and PPF/ELSS contributions under Section 80C, health insurance premiums for self and family under Section 80D, ongoing education loan repayments under Section 80E, and charitable donations under Section 80G. It's particularly valuable for business owners claiming various deductions, parents supporting dependent children's education, and high-income earners with multiple tax-saving investments. Anyone with total deductions exceeding 200000 rupees annually should definitely use this calculator to compare regime benefits."
        },
        {
          title: "How Does This Calculator Work?",
          body: "The calculator aggregates all eligible deductions across Sections 80C, 80D, 80E, and 80G to compute total deductible amounts. It then subtracts these from your gross income to arrive at taxable income. The old regime's tax slabs are then applied: 0% up to 250000 rupees, 5% from 250000-500000 rupees, 20% from 500000-1000000 rupees, and 30% for income above 1000000 rupees. Finally, Health and Education Cess (4% of tax) is added. The result shows detailed breakdown of each deduction type and final tax liability, helping you understand the value of each rupee saved through deductions."
        },
        {
          title: "Key Rules & Regulations",
          body: "The old regime is governed by Chapter VI-A of the Income Tax Act 1961, which contains all Section 80 deduction provisions. Section 80C limits investments to 150000 rupees annually (PPF, ELSS, LIC, home loan principal repayment, etc.), Section 80D covers mediclaim premiums with higher limits for senior citizens, Section 80E provides unlimited deduction for education loan interest paid, and Section 80G allows 50-100% deduction for donations to approved charities. The Finance Act 2023 introduced mandatory ITR filing thresholds that now require filing even below 500000 rupees income if eligible deductions are claimed."
        },
        {
          title: "Maximizing Deductions",
          body: "Strategic planning of deductible expenses maximizes old regime benefits. Coordinate health insurance premium timing with income patterns, ensure education loans are used for qualifying education, maintain PPF/ELSS investments throughout the financial year, and document all donations to eligible charities under Section 80G. Many taxpayers underutilize Section 80E (unlimited education loan interest), Section 80EE (home loan first-time buyers), and Section 80TTA (savings account interest up to 10000 rupees)."
        },
        {
          title: "When to Choose Old Regime?",
          body: "Use the old regime when aggregated deductions under various sections exceed 200000 rupees annually. Working professionals with life insurance, health insurance, and PPF investments often find old regime beneficial. Self-employed individuals with business deductions should always compare both regimes. However, if total deductions are below 150000 rupees, the new regime's lower rates likely provide better savings."
        }
      ],
      faqs: [
        {
          question: "Which deductions are available in old regime?",
          answer:
            "80C: ₹1.5L (PPF, ELSS, LIC, FD), 80D: ₹1.5L (self/family insurance) + ₹1L (parents), 80E: Unlimited (education loan), 80G: 50-100% (donations), 80TTA: ₹10K (savings account interest)",
        },
        {
          question: "Can I claim all deductions together?",
          answer:
            "Yes, you can claim multiple deductions in old regime. Total deduction can exceed ₹3L if you have education loans, donations, and other eligible expenses. No limit on total deductions.",
        },
        {
          question: "Is 80C limit ₹1.5L or higher?",
          answer:
            "Limit is ₹1.5L for all 80C investments combined (PPF, ELSS, LIC, FD, Home Loan Principal). You cannot exceed ₹1.5L total.",
        },
        {
          question: "What is PPF and ELSS for 80C?",
          answer:
            "PPF (Public Provident Fund): ₹500-1.5L/year deposit, 7.1-7.5% return, tax-free. ELSS (Equity Linked Savings Scheme): MF with tax benefits, better returns but volatile.",
        },
        {
          question: "Should I choose old or new regime?",
          answer:
            "Compare: New regime gives relief if deductions < ₹2L. Old regime wins if deductions > ₹2L. Most salaried employees with no major deductions benefit from new regime.",
        }
      ],
      relatedSlugs: [
        "tax/income-tax-calculator",
        "tax/old-vs-new-regime",
        "tax/section-80c-calculator",
      ],
    },
    }),

  // 3. Old vs New Regime Comparison
  createPercentageTool({
    slug: "old-vs-new-regime",
    category: "tax",
    title: "Old vs New Regime Tax Comparison",
    shortTitle: "Regime Comparison",
    description: "Compare tax liability in both regimes and choose the best option for you",
    keywords: ["tax regime", "comparison", "which is better"],
    inputs: [
      {
        type: "number",
        key: "grossIncome",
        label: "Gross Annual Income",
        prefix: "₹",
        min: 0,
        max: 50000000,
        step: 100000,
        default: 1500000,
      },
      {
        type: "number",
        key: "section80C",
        label: "Section 80C Deduction",
        prefix: "₹",
        min: 0,
        max: 150000,
        step: 10000,
        default: 100000,
      },
      {
        type: "number",
        key: "section80D",
        label: "Section 80D (Health Insurance)",
        prefix: "₹",
        min: 0,
        max: 300000,
        step: 10000,
        default: 50000,
      },
      {
        type: "number",
        key: "section80E",
        label: "Section 80E (Education Loan Interest)",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 0,
      },
      {
        type: "number",
        key: "otherDeductions",
        label: "Other Deductions (80G, 80TTA, etc)",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 0,
      }
    ],
    calculate(v) {
      const income = v.grossIncome as number;
      const s80C = v.section80C as number;
      const s80D = v.section80D as number;
      const s80E = v.section80E as number;
      const other = v.otherDeductions as number;

      // NEW REGIME
      const newDeduction = 75000;
      const newTaxableIncome = Math.max(0, income - newDeduction);
      let newTax = 0;
      if (newTaxableIncome <= 400000) {
        newTax = 0;
      } else if (newTaxableIncome <= 800000) {
        newTax = (newTaxableIncome - 400000) * 0.05;
      } else if (newTaxableIncome <= 1200000) {
        newTax = 20000 + (newTaxableIncome - 800000) * 0.1;
      } else if (newTaxableIncome <= 1600000) {
        newTax = 60000 + (newTaxableIncome - 1200000) * 0.15;
      } else if (newTaxableIncome <= 2000000) {
        newTax = 120000 + (newTaxableIncome - 1600000) * 0.2;
      } else if (newTaxableIncome <= 2400000) {
        newTax = 200000 + (newTaxableIncome - 2000000) * 0.25;
      } else {
        newTax = 300000 + (newTaxableIncome - 2400000) * 0.3;
      }
      const newCess = newTax * 0.04;
      const newTotalTax = newTax + newCess;

      // OLD REGIME
      const oldDeductions = s80C + s80D + s80E + other;
      const oldTaxableIncome = Math.max(0, income - oldDeductions);
      let oldTax = 0;
      if (oldTaxableIncome <= 250000) {
        oldTax = 0;
      } else if (oldTaxableIncome <= 500000) {
        oldTax = (oldTaxableIncome - 250000) * 0.05;
      } else if (oldTaxableIncome <= 1000000) {
        oldTax = 12500 + (oldTaxableIncome - 500000) * 0.2;
      } else {
        oldTax = 112500 + (oldTaxableIncome - 1000000) * 0.3;
      }
      const oldCess = oldTax * 0.04;
      const oldTotalTax = oldTax + oldCess;

      const savings = oldTotalTax - newTotalTax;
      const betterRegime = savings > 0 ? "Old Regime" : "New Regime";

      return {
        newTotalTax: Math.round(newTotalTax),
        oldTotalTax: Math.round(oldTotalTax),
        savings: Math.round(Math.abs(savings)),
        betterRegime,
        newTaxableIncome: Math.round(newTaxableIncome),
        oldTaxableIncome: Math.round(oldTaxableIncome),
      };
    },
    resultFields: [
      { key: "newTotalTax", label: "New Regime Tax", format: "currency" },
      { key: "oldTotalTax", label: "Old Regime Tax", format: "currency" },
      { key: "savings", label: "Tax Saving (Better Option)", format: "currency", isPrimary: true },
      { key: "betterRegime", label: "Recommended Regime", format: "text" },
    ],
    chartSegments: [
      { label: "New Regime Tax", valueKey: "newTotalTax", color: "#3b82f6" },
      { label: "Old Regime Tax", valueKey: "oldTotalTax", color: "#ef4444" },
    ],
    content: {
      whatIs:
        "Choosing between the old and new income tax regimes is one of India's most critical annual tax decisions affecting thousands of rupees in take-home income. Since the Finance Act 2023 made the new regime the default option, taxpayers must actively understand which regime suits their financial situation. This comparison calculator evaluates your tax liability under both regimes simultaneously, showing the exact rupee difference in tax paid and helping you identify your optimal choice. The new regime offers lower rates and simplicity suitable for individuals without significant deductions, while the old regime benefits those with substantial life insurance, health insurance premiums, education loan obligations, and investment commitments. Union Budget announcements continue to gradually reduce the attractiveness of the old regime through improved new regime rates, making an annual comparison essential. By using this calculator, you avoid leaving thousands of rupees on the table through poor regime selection and ensure compliance with ITR filing requirements by documenting your choice.",
      sections: [],
      faqs: [
        {
          question: "How do I decide between regimes?",
          answer:
            "Calculate tax in both regimes and choose the lower one. If total eligible deductions > ₹2L, old regime is likely better. If deductions < ₹1.5L, new regime is usually better.",
        },
        {
          question: "Can I change regime after choosing?",
          answer:
            "Yes, you can switch each financial year. If you opt for old regime, you must continue unless you specifically switch back. New regime is default from 2023-24 onwards.",
        },
        {
          question: "Should I make investments just for deductions?",
          answer:
            "No. Invest only if the investment is suitable for your goals and returns (PPF, ELSS, insurance). Don't invest just to get deductions. The tax saving is secondary to good investment.",
        },
        {
          question: "What about future increases in old regime tax rates?",
          answer:
            "Government may increase old regime rates over time. New regime rates are fixed (as announced). This is another consideration - new regime may be future-proof.",
        },
        {
          question: "Do I lose benefits if I switch regimes?",
          answer:
            "No immediate loss, but switching to old regime means no surcharge reduction and higher rates. Switching to new regime means forgoing deductions. Choose based on annual comparison.",
        }
      ],
      relatedSlugs: [
        "tax/income-tax-calculator",
        "tax/income-tax-old-regime",
        "tax/section-80c-calculator",
      ],
    },
    }),

  // 4. Advance Tax Calculator
  createPercentageTool({
    slug: "advance-tax-calculator",
    category: "tax",
    title: "Advance Tax Calculator",
    shortTitle: "Advance Tax",
    description: "Plan and schedule your advance tax payments to avoid penalties",
    keywords: ["advance tax", "quarterly tax", "installments"],
    inputs: [
      {
        type: "number",
        key: "estimatedTaxLiability",
        label: "Estimated Annual Tax Liability",
        prefix: "₹",
        min: 100000,
        max: 50000000,
        step: 100000,
        default: 500000,
        helpText: "Income tax + surcharge + cess for the full year",
      }
    ],
    calculate(v) {
      const tax = v.estimatedTaxLiability as number;

      // Advance tax schedule (quarterly): 15%, 45%, 75%, 100% of estimated tax
      const q1 = Math.round(tax * 0.15);
      const q2 = Math.round(tax * 0.45);
      const q3 = Math.round(tax * 0.75);
      const q4 = Math.round(tax * 1.0);

      return {
        q1June: q1,
        q2September: q2,
        q3December: q3,
        q4March: q4,
        totalTax: tax,
      };
    },
    resultFields: [
      { key: "q1June", label: "Q1 (by June 15)", format: "currency" },
      { key: "q2September", label: "Q2 (by Sep 15)", format: "currency" },
      { key: "q3December", label: "Q3 (by Dec 15)", format: "currency" },
      { key: "q4March", label: "Q4 (by Mar 31)", format: "currency" },
    ],
    content: {
      whatIs:
        "Advance tax is a mandatory quarterly tax payment requirement under Section 207 of the Income Tax Act 1961 for individuals expected to owe tax of 10000 rupees or more during a financial year. This calculator determines when and how much advance tax you must pay in each of the four quarterly installments during the financial year. Unlike the salaried employees who have income tax auto-deducted through TDS monthly, self-employed professionals, business owners, and those with non-salaried income face advance tax obligations. Paying advance tax on schedule is crucial to avoid penalties, interest charges under Section 234B and 234C, and potential scrutiny during tax assessment. The calculator helps you plan cash flow effectively by showing advance tax liability well before payment deadlines and enabling you to structure year-end income strategically. Failing to pay advance tax when due can result in penalties up to 10% of shortfall, making this calculator an essential tool for self-employed and business income earners in India.",
      sections: [],
      faqs: [
        {
          question: "Who needs to pay advance tax?",
          answer:
            "Individuals, HUFs, and partnerships with tax liability exceeding ₹10,000 in a financial year. Self-employed, business owners, and investors typically pay advance tax.",
        },
        {
          question: "What happens if I don't pay advance tax?",
          answer:
            "Interest at 1% per month (12% p.a.) is charged on the shortfall. Also, penalties and possible prosecution for tax evasion. It's mandatory to avoid complications.",
        },
        {
          question: "How do I calculate my estimated tax liability?",
          answer:
            "Estimate your annual income, calculate tax using applicable slabs, add surcharge and cess. If uncertain, use a tax consultant or online tools to calculate.",
        },
        {
          question: "Can I revise my advance tax estimate?",
          answer:
            "Yes, you can file revised returns if your income changes. However, undue delay in paying advance tax can attract interest. Pay on time to avoid complications.",
        },
        {
          question: "Is advance tax deductible from final tax liability?",
          answer:
            "Yes, advance tax paid is fully credited against your final tax liability in ITR. If you overpaid, excess is refunded.",
        }
      ],
      relatedSlugs: [
        "tax/income-tax-calculator",
        "tax/income-tax-old-regime",
        "tax/tax-saving-calculator",
      ],
    },
    }),

  // 5. TDS Calculator (General)
  createPercentageTool({
    slug: "tds-calculator",
    category: "tax",
    title: "TDS Calculator (General)",
    shortTitle: "TDS General",
    description: "Calculate tax deducted at source on various income types",
    keywords: ["TDS", "tax deduction", "source"],
    inputs: [
      {
        type: "select",
        key: "tdsType",
        label: "TDS Type",
        options: [
          { value: "salary", label: "Salary" },
          { value: "rent", label: "Rent (194I)" },
          { value: "interest", label: "Interest (194A)" },
          { value: "commission", label: "Commission (194H)" },
          { value: "professional", label: "Professional Fees (194J)" },
          { value: "property", label: "Property Sale (194LA)" },
        ],
        default: "salary",
      },
      {
        type: "number",
        key: "paymentAmount",
        label: "Payment Amount",
        prefix: "₹",
        min: 1000,
        max: 100000000,
        step: 10000,
        default: 500000,
      }
    ],
    calculate(v) {
      const type = v.tdsType as string;
      const amount = v.paymentAmount as number;

      const tdsRates: Record<string, number> = {
        salary: 0, // Calculated based on income slab by employer
        rent: 0.1, // 10% if rent > ₹50K/month
        interest: 0.1, // 10% on bank interest
        commission: 0.05, // 5% on brokerage/commission
        professional: 0.1, // 10% on professional fees
        property: 0.01, // 1% on property sale > ₹50L
      };

      const tdsRate = tdsRates[type] || 0;
      const tdsAmount = amount * tdsRate;

      return {
        paymentAmount: amount,
        tdsRate: Math.round(tdsRate * 100 * 100) / 100,
        tdsAmount: Math.round(tdsAmount),
        netPayment: Math.round(amount - tdsAmount),
        tdsType: type,
      };
    },
    resultFields: [
      { key: "tdsAmount", label: "TDS Amount", format: "currency", isPrimary: true },
      { key: "netPayment", label: "Net Payment to You", format: "currency" },
      { key: "tdsRate", label: "TDS Rate", format: "percent" },
    ],
    content: {
      whatIs:
        "Tax Deducted at Source (TDS) is a mechanism where tax is collected at the point of transaction itself, making you receive only net income after tax. TDS applies across various income categories including salary (deducted by employers monthly), rent (deducted by tenants), professional fees (deducted by clients), interest on fixed deposits (deducted by banks), and commission/brokerage. This calculator helps you understand how much TDS will be deducted on your income under different scenarios, essential for financial planning and ITR filing. Each income type has different TDS rates and applicability conditions under various Sections of the Income Tax Act 1961. Understanding TDS is critical because it represents advance tax payment credited to your account, reducing your final tax liability or providing refunds after ITR filing. Many taxpayers overpay through excess TDS and obtain substantial refunds by filing accurate ITR claims.",
      sections: [],
      faqs: [
        {
          question: "Is TDS deducted on all payments?",
          answer:
            "No, only on specified payments exceeding certain thresholds. Rent >₹50K/month, interest from banks, professional fees, commission, etc. Small transactions (<₹10K) are often not TDS-liable.",
        },
        {
          question: "Can I get TDS refund?",
          answer:
            "Yes, if TDS deducted exceeds your actual tax liability. This is refunded when you file your ITR. TDS becomes a credit against your tax liability.",
        },
        {
          question: "What happens if TDS is not deducted when it should be?",
          answer:
            "The deductor is liable for penalty. If TDS is not deducted, include the payment in your income and pay tax. You can also approach the deductor to deduct and deposit TDS.",
        },
        {
          question: "Do I need to report TDS in my ITR?",
          answer:
            "Yes, all TDS received should be reported in ITR under the 'Income from Other Sources' or relevant sections. Include TDS certificates (Form 16, 16A) for verification.",
        },
        {
          question: "Can I claim exemption from TDS?",
          answer:
            "Yes, by submitting a declaration (Form 15G/15H for non-taxable individuals). If your income is below taxable limit, you can get exemption from TDS.",
        }
      ],
      relatedSlugs: [
        "tax/tds-on-salary",
        "tax/tds-on-rent",
        "tax/tds-on-professional-fees",
      ],
    },
    }),

  // 6. TDS on Salary
  createPercentageTool({
    slug: "tds-on-salary",
    category: "tax",
    title: "TDS on Salary Calculator",
    shortTitle: "Salary TDS",
    description: "Understand TDS on salary deductions and optimize your take-home pay",
    keywords: ["TDS on salary", "salary deduction", "income tax"],
    amountLabel: "Gross Monthly Salary",
    rateLabel: "Estimated Tax Rate",
    resultLabel: "Estimated TDS Deduction",
    defaultAmount: 100000,
    maxAmount: 2000000,
    defaultRate: 10,
    maxRate: 30,
    whatIs:
      "Tax Deducted at Source on salary (TDS) is the income tax automatically deducted by employers from your monthly salary through a process called salary withholding. This ensures advance payment of tax throughout the financial year rather than a lump-sum payment at year-end. Employers calculate TDS using tax tables prescribed by the Income Tax Department, taking into account your salary structure, age, HRA exemptions, and specified deductions you claim. The TDS on salary is determined using Form 12BB or declarations you submit to your employer, informing them about expected deductions and exemptions applicable to you. Under the new regime, TDS is calculated on salary after standard deduction of 75000 rupees, while the old regime allows deductions for investments and insurance. Understanding your TDS helps ensure you're not overpaying through excessive deductions or underpaying leading to year-end tax liability. This calculator breaks down exactly how salary TDS is computed, helping you verify employer calculations and estimate year-end tax position.",
    faqs: [
      {
        question: "How is TDS on salary calculated?",
        answer:
          "Your employer estimates your annual income, calculates tax on it (using ITR-1 or internal calculation), and deducts proportional TDS monthly. This is an estimate; final tax is calculated on ITR filing.",
      },
      {
        question: "Can I claim relief if TDS is over-deducted?",
        answer:
          "Yes, if TDS exceeds your actual tax liability (due to deductions), you get refund on ITR filing. This is common if you have 80C investments or medical insurance.",
      },
      {
        question: "Is TDS deducted on HRA and other allowances?",
        answer:
          "TDS is deducted on total salary (salary + allowances - deductions). HRA is part of salary and taxable (but exempt to the extent of actual rent minus 10% of salary).",
      },
      {
        question: "Should I submit Form 12BB for TDS calculation?",
        answer:
          "Yes, submit Form 12BB if you claim exemptions (HRA) or have significant deductions. This helps your employer calculate correct TDS instead of over-deducting.",
      },
      {
        question: "What is Form 16 and when do I get it?",
        answer:
          "Form 16 is your salary certificate issued by the employer showing gross salary, deductions, and TDS deducted. Get it by May 31 of the following financial year for ITR filing.",
      }
    ],
    relatedSlugs: [
      "tax/tds-calculator",
      "tax/hra-exemption-calculator",
      "tax/income-tax-calculator",
    ],
  }),

  // 7. TDS on Rent
  createPercentageTool({
    slug: "tds-on-rent",
    category: "tax",
    title: "TDS on Rent Calculator (Section 194I)",
    shortTitle: "Rent TDS",
    description: "Calculate TDS payable when paying rent to landlords",
    keywords: ["TDS on rent", "rent deduction", "194I"],
    amountLabel: "Monthly Rent Amount",
    rateLabel: "TDS Rate",
    resultLabel: "TDS on Rent",
    defaultAmount: 100000,
    maxAmount: 500000,
    defaultRate: 10,
    maxRate: 10,
    whatIs:
      "Tax Deducted at Source on rent is mandatory when you pay house rent exceeding 50000 rupees monthly (600000 rupees annually) to a residential landlord who doesn't have an ITR filing requirement or hasn't provided their PAN. Under Section 194I of the Income Tax Act 1961, a tenant must deduct 10% TDS on rent payments (or 20% if PAN not provided) and deposit it with income tax authorities. This calculator determines your exact TDS obligation on rent payments, essential for both tenants and landlords. For tenants, understanding TDS is important because it represents advance tax paid on the landlord's behalf, which the landlord must reflect in their ITR filings for refund claims or adjustment. Landlords without proper documentation from tenants may receive inadequate TDS, affecting their financial planning. Both parties should maintain clear records and documentation, with the calculator helping verify correct TDS amount at each month-end. Non-compliance with TDS requirements on rent can lead to penalties for both parties under Section 271H.",
    faqs: [
      {
        question: "Who should deduct TDS on rent?",
        answer:
          "Any individual who pays rent >₹50,000/month to another individual should deduct TDS. Payments to corporate landlords, societies, or government entities don't require TDS.",
      },
      {
        question: "Is TDS deducted if rent is split among multiple people?",
        answer:
          "If multiple tenants pay collectively >₹50K/month to one landlord, TDS must still be deducted. If payments are <₹50K individually, TDS is not required.",
      },
      {
        question: "Can landlord claim exemption from TDS?",
        answer:
          "Yes, if the landlord's total income is below taxable limit or expects no tax liability, they can submit Form 15H. TDS can then be waived.",
      },
      {
        question: "Is TDS deducted upfront or at year-end?",
        answer:
          "TDS is deducted from each rent payment. For ₹100K monthly rent, ₹10K TDS is deducted, and ₹90K net is paid to the landlord.",
      },
      {
        question: "What documents are needed for TDS on rent?",
        answer:
          "PAN of both tenant and landlord, rental agreement, and proof of payments. TDS deposited should be reported in Form 27EQ by the tenant's bank/employer if applicable.",
      }
    ],
    relatedSlugs: [
      "tax/tds-calculator",
      "tax/tds-on-professional-fees",
      "property/rental-yield-calculator",
    ],
  }),

  // 8. TDS on Professional Fees
  createPercentageTool({
    slug: "tds-on-professional-fees",
    category: "tax",
    title: "TDS on Professional Fees Calculator (Section 194J)",
    shortTitle: "Professional Fees TDS",
    description: "Calculate TDS obligations on payments for professional services",
    keywords: ["TDS on professional fees", "194J", "professional deduction"],
    amountLabel: "Professional Fees Amount",
    rateLabel: "TDS Rate",
    resultLabel: "TDS on Fees",
    defaultAmount: 100000,
    maxAmount: 10000000,
    defaultRate: 10,
    maxRate: 10,
    whatIs:
      "Tax Deducted at Source on professional fees applies when organizations or individuals pay fees to lawyers, chartered accountants, consultants, doctors, and other professionals exceeding 50000 rupees in a financial year. Under Section 194J of the Income Tax Act 1961, payers must deduct TDS at 10% (20% if PAN not available) on professional service payments and remit this to the tax department. This calculator helps service providers and organizations understand TDS implications when engaging professional services, essential for accurate billing and cash flow management. For professionals, understanding TDS helps determine net income after tax deduction from client payments, affecting pricing decisions and financial planning. Organizations must maintain proper TDS documentation and comply with quarterly deposit requirements under TDS rules. Failure to deduct or deposit TDS on professional fees can result in penalties up to 10% of tax not deducted. This calculator provides clarity on TDS obligations and helps both service consumers and providers maintain compliant financial practices.",
    faqs: [
      {
        question: "Who pays TDS on professional fees?",
        answer:
          "The client (payer) deducts TDS at 10% when paying professionals. If annual fees >₹30K, TDS becomes mandatory.",
      },
      {
        question: "Are all professionals liable for TDS?",
        answer:
          "Yes, if they are individuals (not firms/companies). Payments to partnership firms or companies don't require TDS on professional fees.",
      },
      {
        question: "Can a professional claim exemption?",
        answer:
          "Yes, if expected taxable income is below the slabs, the professional can submit Form 15H to get exemption from TDS.",
      },
      {
        question: "How is TDS on professional fees different from business payment TDS?",
        answer:
          "TDS on professional fees (194J) is 10%, applies to individuals, threshold ₹30K annually. Other business payments have different TDS rates (e.g., 1% on brokerage, 2% on sub-contractor fees).",
        },
        {
          question: "What if I pay through multiple installments below ₹30K?",
          answer:
            "If aggregate payments exceed ₹30K in a financial year, TDS becomes due from the date it exceeds ₹30K. Deliberate splitting to avoid TDS can lead to penalties.",
        },
      ],
    relatedSlugs: [
      "tax/tds-calculator",
      "tax/tds-on-rent",
      "business/professional-tax",
    ],
  }),

  // 9. TDS Interest & Penalty
  createPercentageTool({
    slug: "tds-interest-penalty",
    category: "tax",
    title: "TDS Late Payment Interest & Penalty Calculator",
    shortTitle: "TDS Interest & Penalty",
    description: "Calculate penalties and interest on delayed or missing TDS payments",
    keywords: ["TDS interest", "TDS penalty", "late payment"],
    inputs: [
      {
        type: "number",
        key: "tdsAmount",
        label: "TDS Amount",
        prefix: "₹",
        min: 10000,
        max: 10000000,
        step: 50000,
        default: 500000,
      },
      {
        type: "number",
        key: "daysLate",
        label: "Days Late (from due date)",
        suffix: "days",
        min: 1,
        max: 365,
        step: 1,
        default: 30,
      }
    ],
    calculate(v) {
      const tds = v.tdsAmount as number;
      const daysLate = v.daysLate as number;

      // Interest: 1% per month (0.0333% per day)
      const monthsLate = daysLate / 30;
      const interest = tds * 0.01 * monthsLate;

      // Penalty: 10% for failure to deposit TDS
      const penalty = tds * 0.1;

      // Total
      const totalLiability = tds + interest + penalty;

      return {
        tdsAmount: tds,
        interest: Math.round(interest),
        penalty: Math.round(penalty),
        totalLiability: Math.round(totalLiability),
        daysLate,
      };
    },
    resultFields: [
      { key: "interest", label: "Interest @ 1% p.m.", format: "currency" },
      { key: "penalty", label: "Penalty @ 10%", format: "currency" },
      { key: "totalLiability", label: "Total Liability", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs:
        "The Income Tax Department levies penalties and interest charges on taxpayers who fail to deduct or deposit TDS on time, adding significant financial burden beyond the original tax liability. Section 234B imposes interest at 1% per month (or part thereof) on late TDS deposits, while Section 234C levies penalty for shortfall in advance tax payment. Additionally, Section 271H imposes flat penalties for various TDS failures ranging from 50% to 200% of undeducted TDS amount, escalating based on violation severity and non-compliance history. For honest mistakes or delays, the department offers relief under Section 234E if TDS and taxes are paid within due date or at least 30 days before assessment. This calculator helps you understand the true cost of TDS non-compliance and plan proper payments to avoid these punitive charges. Many taxpayers underestimate penalty and interest liabilities that often exceed the original TDS amount, making this calculator crucial for financial planning and compliance strategy.",
      sections: [],
      faqs: [
        {
          question: "What is the due date for TDS deposit?",
          answer:
            "TDS is due by the 7th of the next month for salary (monthly), quarterly TDS by 15th of next quarter, quarterly TDS for contractors by 7th. Deposits after this date attract interest.",
        },
        {
          question: "Is the 10% penalty mandatory?",
          answer:
            "Yes, 10% penalty is automatic for failure to deposit TDS. There's no relief or exemption. Only advance intimation to tax dept might reduce penalty in rare cases.",
        },
        {
          question: "Can I deposit TDS without penalty if I'm late?",
          answer:
            "No, penalty is automatic. However, interest may be waived in some circumstances if there's genuine hardship. Always deposit on time to avoid both interest and penalty.",
        },
        {
          question: "What happens if TDS is not deposited for years?",
          answer:
            "Prosecution is possible. The responsible person (employer, payer) can face imprisonment. Always deposit TDS on time to avoid serious legal consequences.",
        },
        {
          question: "Is there relief for first-time TDS default?",
          answer:
            "Some states offer 50% penalty waiver for first-time defaulters if paid within a specific period. Check with your state tax department for applicable relief schemes.",
        }
      ],
      relatedSlugs: [
        "tax/tds-calculator",
        "tax/tds-on-salary",
        "tax/advance-tax-calculator",
      ],
    },
    }),

  // 10. GST Calculator
  createPercentageTool({
    slug: "gst-calculator",
    category: "tax",
    title: "GST Calculator",
    shortTitle: "GST",
    description: "Calculate GST on products and services under different tax rates",
    keywords: ["GST", "goods and services tax", "tax calculation"],
    amountLabel: "Pre-GST Amount",
    rateLabel: "GST Rate",
    resultLabel: "GST Amount",
    defaultAmount: 100000,
    maxAmount: 10000000,
    defaultRate: 18,
    maxRate: 28,
    whatIs:
      "Goods and Services Tax (GST) is India's unified consumption tax applicable on goods and services, effective from July 1, 2017, replacing multiple indirect taxes. The calculator determines final prices including GST at applicable rates (5%, 12%, 18%, or 28%) depending on item classification under the GST Tariff code system. Understanding GST is essential for consumers evaluating product costs, businesses determining pricing strategies, and traders ensuring accurate tax compliance. Different product categories attract different GST rates: food items at 5%, apparel and medicines at 5-12%, electronics and vehicles at 18%, and luxury items at 28%. Input tax credit mechanism allows registered businesses to deduct GST paid on purchases from GST collected on sales, reducing net tax liability. For consumers and businesses managing budgets, this calculator clarifies the tax component in product prices and helps understand why some items cost more than others. Whether you're buying groceries, electronics, or services, understanding the GST component impacts financial planning and decision-making.",
    faqs: [
      {
        question: "What are the main GST slabs in India?",
        answer:
          "5% on essentials (food, medicines), 12% on middle-category items, 18% on most goods/services, 28% on luxury items (cars, jewelry). Some items are 0% (Nil rate).",
      },
      {
        question: "Who pays GST - buyer or seller?",
        answer:
          "The end consumer bears the GST cost, but registered businesses collect and remit it to the government. Businesses can claim input GST credit on their purchases.",
      },
      {
        question: "Is every business required to register for GST?",
        answer:
          "Businesses with annual turnover >₹40L (₹10L for NE states) must register. Lower turnover businesses can opt for voluntary registration.",
      },
      {
        question: "Can I claim input GST credit?",
        answer:
          "Yes, if you're GST registered and purchase inputs/services for business. Input credit is deducted from output GST collected, so you pay tax only on value added.",
      },
      {
        question: "How do I calculate GST on a price?",
        answer:
          "If price is pre-GST (₹100, 18% GST), add 18% to get ₹118. If price is GST-inclusive (₹118), divide by 1.18 to get pre-GST price (₹100).",
        }
    ],
    relatedSlugs: [
      "tax/gst-inclusive-exclusive",
      "business/business-tax",
      "tax/tds-calculator",
    ],
  }),

  // 11. GST Inclusive/Exclusive Converter
  createPercentageTool({
    slug: "gst-inclusive-exclusive",
    category: "tax",
    title: "GST Inclusive-Exclusive Converter",
    shortTitle: "GST Converter",
    description: "Convert prices between GST-inclusive and GST-exclusive amounts",
    keywords: ["GST conversion", "inclusive exclusive", "price calculation"],
    inputs: [
      {
        type: "radio",
        key: "priceType",
        label: "Price Type",
        options: [
          { value: "inclusive", label: "GST Inclusive" },
          { value: "exclusive", label: "GST Exclusive" },
        ],
        default: "inclusive",
      },
      {
        type: "number",
        key: "price",
        label: "Price Amount",
        prefix: "₹",
        min: 100,
        max: 10000000,
        step: 100,
        default: 118,
      },
      {
        type: "number",
        key: "gstRate",
        label: "GST Rate",
        suffix: "%",
        min: 0,
        max: 28,
        step: 1,
        default: 18,
      }
    ],
    calculate(v) {
      const price = v.price as number;
      const gstRate = v.gstRate as number;
      const priceType = v.priceType as string;

      let basePrice: number;
      let gstAmount: number;
      let finalPrice: number;

      if (priceType === "inclusive") {
        // If inclusive, remove GST to get base
        basePrice = price / (1 + gstRate / 100);
        gstAmount = price - basePrice;
        finalPrice = price;
      } else {
        // If exclusive, add GST
        basePrice = price;
        gstAmount = price * (gstRate / 100);
        finalPrice = price + gstAmount;
      }

      return {
        basePrice: Math.round(basePrice),
        gstAmount: Math.round(gstAmount),
        finalPrice: Math.round(finalPrice),
        gstRate,
        priceType,
      };
    },
    resultFields: [
      { key: "basePrice", label: "Base Price (Excl. GST)", format: "currency" },
      { key: "gstAmount", label: "GST Amount", format: "currency" },
      { key: "finalPrice", label: "Final Price (Incl. GST)", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs:
        "In India's GST system, prices are often quoted either as GST-exclusive (base price before tax) or GST-inclusive (final price including tax), creating confusion during transactions. This calculator instantly converts between these two formats across all GST rate brackets (5%, 12%, 18%, 28%), essential for B2B transactions, invoicing, and price comparisons. When retailers show prices without GST, the actual cost to consumers is higher; when showing GST-inclusive prices, the tax component is built-in. Businesses issuing invoices must clearly separate GST-exclusive base amount from GST collected as per GST Rules, making conversion accuracy critical. For consumers comparing products from different sellers quoting in different formats, this calculator ensures accurate price comparison. International traders importing goods into India and domestic businesses exporting face this conversion regularly for proper invoice documentation. Accurate GST-inclusive and exclusive price understanding prevents disputes, ensures proper tax compliance, and enables fair pricing decisions.",
      sections: [],
      faqs: [
        {
          question: "Why do businesses quote exclusive prices?",
          answer:
            "GST-exclusive prices make it clear how much of the amount goes to GST vs the actual product cost. This is standard in B2B invoicing and important for input credit tracking.",
        },
        {
          question: "How do consumers see prices?",
          answer:
            "Consumers see GST-inclusive prices in retail. E-commerce, stores show final price including GST. You don't separately pay GST; it's already included.",
        },
        {
          question: "Is the conversion formula always correct?",
          answer:
            "Yes, the formula is constant: Inclusive = Exclusive × (1 + GST%), Exclusive = Inclusive / (1 + GST%). Use these for accurate conversions.",
        },
        {
          question: "What if a product has 0% GST?",
          answer:
            "0% GST means no tax is added. Exclusive and inclusive prices are the same. Common for food items, medicines, and books.",
        },
        {
          question: "Can I recover GST on purchases?",
          answer:
            "Only if you're GST registered and purchase is for business. Input GST credit is deducted from output GST. Consumers cannot claim GST credit.",
        }
      ],
      relatedSlugs: [
        "tax/gst-calculator",
        "business/business-tax",
        "tax/tds-calculator",
      ],
    },
    }),

  // 12. HRA Exemption Calculator
  createPercentageTool({
    slug: "hra-exemption-calculator",
    category: "tax",
    title: "HRA Exemption Calculator",
    shortTitle: "HRA Exemption",
    description: "Calculate tax-free HRA exemption available to salaried employees",
    keywords: ["HRA", "exemption", "house rent allowance"],
    inputs: [
      {
        type: "number",
        key: "salary",
        label: "Basic Salary + DA",
        prefix: "₹",
        min: 0,
        max: 5000000,
        step: 10000,
        default: 500000,
        helpText: "Basic + Dearness Allowance (excluding other allowances)",
      },
      {
        type: "number",
        key: "hra",
        label: "HRA Received",
        prefix: "₹",
        min: 0,
        max: 1000000,
        step: 10000,
        default: 75000,
        helpText: "Monthly HRA amount",
      },
      {
        type: "number",
        key: "rent",
        label: "Actual Rent Paid Monthly",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 80000,
        helpText: "Actual rent to landlord",
      },
      {
        type: "radio",
        key: "city",
        label: "City Type",
        options: [
          { value: "metro", label: "Metro City (50% of salary)" },
          { value: "non-metro", label: "Non-Metro (40% of salary)" },
        ],
        default: "metro",
      }
    ],
    calculate(v) {
      const salary = v.salary as number;
      const hra = v.hra as number;
      const rent = v.rent as number;
      const city = v.city as string;

      // HRA Exemption: Least of (1) HRA received, (2) Rent - 10% of salary, (3) 50% (metro) or 40% (non-metro) of salary
      const percentageRule = city === "metro" ? salary * 0.5 : salary * 0.4;
      const rentRule = Math.max(0, rent - salary * 0.1);

      const exemption = Math.min(hra, rentRule, percentageRule);
      const taxableHRA = hra - exemption;

      return {
        hra,
        rule1HRA: hra,
        rule2Rent: Math.round(rentRule),
        rule3Percentage: Math.round(percentageRule),
        exemption: Math.round(exemption),
        taxableHRA: Math.round(taxableHRA),
        city,
      };
    },
    resultFields: [
      { key: "exemption", label: "HRA Exemption", format: "currency", isPrimary: true },
      { key: "taxableHRA", label: "Taxable HRA", format: "currency" },
      { key: "rule1HRA", label: "Rule 1: HRA Received", format: "currency" },
      { key: "rule2Rent", label: "Rule 2: Rent - 10% Salary", format: "currency" },
      { key: "rule3Percentage", label: "Rule 3: % of Salary", format: "currency" },
    ],
    content: {
      whatIs:
        "House Rent Allowance (HRA) is a significant component of salaried employee compensation, and Section 10(13A) of the Income Tax Act 1961 allows a portion of HRA to be exempt from taxation based on actual rent paid. This exemption is calculated as the minimum of three factors: actual HRA received, 50% of salary (in metro cities) or 40% of salary (in non-metro cities), and actual rent paid minus 10% of salary. This calculator determines your exact HRA tax-free amount considering metro/non-metro status, salary components, and rent paid to landlord. The HRA exemption calculation requires accurate documentation including rent receipts, landlord PAN, and salary slips, making this calculator an essential planning tool. Many salaried employees miss significant tax savings by not claiming full eligible HRA exemptions or not understanding complex calculation rules. The exemption varies annually based on changing rent, salary increases, and city classification changes. For employees planning rent agreements, salary negotiations, or tax compliance, this calculator provides clarity on potential savings ranging from 50000 to several lakhs annually depending on salary and rent levels.",
      sections: [],
      faqs: [
        {
          question: "What is included in basic salary for HRA calculation?",
          answer:
            "Only basic salary and dearness allowance (DA) are included. Other allowances (travel, medical, performance bonus) are excluded from HRA calculation.",
        },
        {
          question: "Does HRA depend on the city I live in?",
          answer:
            "Yes, the percentage rule varies: 50% of basic+DA for metro cities (Delhi, Mumbai, Chennai, Bangalore), 40% for other cities, 25% for smaller towns (some companies have different grids).",
        },
        {
          question: "What counts as 'actual rent paid'?",
          answer:
            "Rent paid to the landlord for residential accommodation only. Registration of leave and license or rental agreement helps prove actual rent.",
        },
        {
          question: "Can I claim HRA without a rental agreement?",
          answer:
            "Technically yes, but having a registered agreement strengthens your claim during income tax audit. Without proof, assessing officer may disallow the exemption.",
        },
        {
          question: "What if I don't pay rent (live with family)?",
          answer:
            "If no rent is paid (Rule 2 becomes zero), you can claim only Rule 3 exemption (% of salary). Most of HRA becomes taxable.",
        }
      ],
      relatedSlugs: [
        "tax/income-tax-calculator",
        "tax/tds-on-salary",
        "tax/income-tax-old-regime",
      ],
    },
    }),

  // Remaining tax tools (13-30) - condensed versions using createPercentageTool where applicable
  // 13. Section 80C Calculator
  createPercentageTool({
    slug: "section-80c-calculator",
    category: "tax",
    title: "Section 80C Deduction Planning Calculator",
    shortTitle: "80C Planning",
    description: "Plan investments to maximize Section 80C deduction benefits",
    keywords: ["80C", "deduction", "investment"],
    amountLabel: "Investment Amount",
    rateLabel: "Tax Rate (Your Slab)",
    resultLabel: "Tax Saving",
    defaultAmount: 150000,
    maxAmount: 150000,
    defaultRate: 30,
    maxRate: 30,
    whatIs:
      "Section 80C of the Income Tax Act 1961 is the most popular and generous tax deduction provision, allowing individuals to reduce taxable income by up to 150000 rupees through specific investments and expenses. Eligible avenues include Public Provident Fund (PPF) returns and principal, Employee Provident Fund (EPF) contributions, Equity Linked Savings Schemes (ELSS) in mutual funds, life insurance policy premiums, fixed deposit principal repayment on home loans, and children's tuition fees. The 150000 rupee cap is a combined limit across all Section 80C avenues, meaning you cannot invest 150000 rupees in PPF and additionally 150000 rupees in life insurance. This calculator helps you plan optimal allocation of investments across available 80C avenues to maximize tax savings while aligning with financial goals. Understanding Section 80C is crucial because strategic 150000 rupee investment can save 30000-50000 rupees in taxes annually while building long-term wealth through PPF or ELSS returns. The majority of Indian taxpayers have total investments exceeding 150000 rupees, making investment prioritization and planning essential to maximize tax benefits. Union Budget 2024-25 continues to emphasize long-term savings through enhanced 80C benefits.",
    sections: [],
    faqs: [
      {
        question: "Which investments qualify for 80C deduction?",
        answer:
          "PPF, ELSS mutual funds, life insurance premiums, NSC, post office schemes, fixed deposits (5 years), tuition fees, home loan principal, sukanya samriddhi. PPF and ELSS are most popular.",
      },
      {
        question: "Can I claim 80C without investment?",
        answer:
          "No, 80C is specifically for investments. You must invest to claim deduction. Home loan principal payments also qualify as investment.",
      },
      {
        question: "What is the best 80C investment?",
        answer:
          "ELSS mutual funds offer good returns (8-12% avg) and tax deduction, with only 3-year lock-in. PPF is safer but lower returns (7-8%). Choose based on risk appetite.",
      },
      {
        question: "Can I claim 80C multiple times?",
        answer:
          "No, total limit is ₹1.5L per financial year across all 80C investments. You cannot exceed this limit even with multiple investments.",
      },
      {
        question: "Should I invest just for tax saving?",
        answer:
          "No, only invest if the scheme is suitable. Tax saving is a bonus. A bad ELSS investment with poor returns is worse than paying tax on undeducted income.",
        }
    ],
    relatedSlugs: [
      "tax/section-80d-calculator",
      "tax/income-tax-old-regime",
      "investment/elss-mutual-fund",
    ],
  }),

  // 14. Section 80D Calculator
  createPercentageTool({
    slug: "section-80d-calculator",
    category: "tax",
    title: "Section 80D Health Insurance Deduction Calculator",
    shortTitle: "80D Planning",
    description: "Calculate tax savings from health insurance premiums under Section 80D",
    keywords: ["80D", "health insurance", "medical deduction"],
    amountLabel: "Annual Health Insurance Premium",
    rateLabel: "Your Tax Slab",
    resultLabel: "Tax Saving",
    defaultAmount: 100000,
    maxAmount: 300000,
    defaultRate: 20,
    maxRate: 30,
    whatIs:
      "Section 80D of the Income Tax Act 1961 provides deduction for health insurance premiums paid by individuals for self, spouse, and dependent children up to 150000 rupees annually, plus additional 100000 rupees for parents' mediclaim if they're senior citizens (over 60 years). The combined limit for self/family is 150000 rupees, and additional 100000 rupees applies only if you're funding parents' health insurance from your income. This deduction is available regardless of whether you claim deductions or exemptions under old or new regimes, making it a rare deduction available under both systems. Eligible health insurance includes mediclaim policies from any insurance company, domiciliary treatment expenses, and pre-hospitalization treatment costs. The calculator determines your maximum Section 80D deduction by verifying policy holders (self, spouse, children, parents), senior citizen status, and claim amounts, ensuring compliance with limits. With healthcare costs rising, strategic health insurance investment can provide dual benefits: health protection plus tax savings of 30000-50000 rupees annually. Parents' health insurance deduction is especially valuable for middle-class families supporting aging parents, turning responsibility into tax-efficient planning.",
    sections: [],
    faqs: [
      {
        question: "What is the 80D deduction limit?",
        answer:
          "₹1.5L for self + spouse + dependent children, plus additional ₹1L for parents' insurance. Total ₹2.5L if parents are covered. Married couples get separate limits.",
      },
      {
        question: "Does health insurance cover need to show proof?",
        answer:
          "Yes, you must have active health insurance policy. File ITR with insurance policy details. Claim receipts of premium payments as proof.",
      },
      {
        question: "Which health insurance policies qualify?",
        answer:
          "IRDA-approved health insurance from registered insurers. Government schemes like AYUSHMAN BHARAT and PMJAY also qualify.",
      },
      {
        question: "Can I claim 80D without actual claim?",
        answer:
          "Yes, 80D is for premium paid, not for claims made. Even if you don't claim benefits, paying premium entitles deduction.",
      },
      {
        question: "What about critical illness insurance?",
        answer:
          "Critical illness insurance by itself doesn't qualify for 80D. It must be ancillary to base health insurance. Some policies combine both.",
        }
    ],
    relatedSlugs: [
      "tax/section-80c-calculator",
      "tax/income-tax-old-regime",
      "insurance/health-insurance",
    ],
  }),

  // 15. LTCG Tax Calculator
  createPercentageTool({
    slug: "ltcg-tax-calculator",
    category: "tax",
    title: "Long-Term Capital Gains Tax Calculator",
    shortTitle: "LTCG Tax",
    description: "Calculate tax on long-term capital gains from stock and property sales",
    keywords: ["LTCG", "capital gains", "equity tax"],
    inputs: [
      {
        type: "number",
        key: "purchasePrice",
        label: "Purchase Price",
        prefix: "₹",
        min: 100000,
        max: 50000000,
        step: 100000,
        default: 1000000,
      },
      {
        type: "number",
        key: "sellingPrice",
        label: "Selling Price",
        prefix: "₹",
        min: 100000,
        max: 50000000,
        step: 100000,
        default: 1500000,
      },
      {
        type: "number",
        key: "yearsHeld",
        label: "Years Held (for indexation)",
        suffix: "years",
        min: 2,
        max: 50,
        step: 1,
        default: 5,
      }
    ],
    calculate(v) {
      const buyPrice = v.purchasePrice as number;
      const sellPrice = v.sellingPrice as number;
      const years = v.yearsHeld as number;

      // LTCG tax: 20% with indexation benefit
      const inflationRate = 0.05; // Avg 5% inflation
      const indexedCost = buyPrice * Math.pow(1 + inflationRate, years);
      const ltcgGain = sellPrice - indexedCost;
      const tax = Math.max(0, ltcgGain * 0.2); // 20% tax

      return {
        gain: Math.round(sellPrice - buyPrice),
        indexedCost: Math.round(indexedCost),
        ltcgGain: Math.round(ltcgGain),
        tax: Math.round(tax),
        netProceeds: Math.round(sellPrice - tax),
      };
    },
    resultFields: [
      { key: "ltcgGain", label: "Taxable LTCG", format: "currency" },
      { key: "tax", label: "LTCG Tax @ 20%", format: "currency", isPrimary: true },
      { key: "netProceeds", label: "Net Proceeds After Tax", format: "currency" },
    ],
    content: {
      whatIs:
        "Long-Term Capital Gains (LTCG) arise when you sell investment assets (stocks, mutual funds, property, bonds) held for more than specified holding periods (1-3 years depending on asset type) at profit. India provides favorable tax treatment for long-term gains: equity shares/mutual fund units attract 20% tax with indexation benefit reducing inflation impact, property held over 2 years attracts 20% tax with indexation, and gold held over 3 years attracts 20% tax. The indexation benefit calculates inflation-adjusted cost, significantly reducing taxable gains especially for assets held 5+ years. This calculator determines your exact LTCG tax liability considering holding period, asset type, purchase price, sale price, and indexation factors. For investors and property owners planning asset sales, understanding LTCG taxation through this calculator helps optimize timing and pricing strategies to minimize tax impact. Many taxpayers miss indexation benefits or hold assets unnecessarily long, missing better investment opportunities. The Finance Act 2024 introduced new LTCG provisions, making regular update and recalculation important for accurate compliance.",
      sections: [],
      faqs: [
        {
          question: "What is indexation benefit?",
          answer:
            "Indexation adjusts your purchase price for inflation. If you bought at ₹100L and held for 5 years at 5% inflation, indexed cost becomes ₹127L, reducing taxable gain.",
        },
        {
          question: "Can I claim exemption on LTCG from property?",
          answer:
            "Yes, one self-occupied residential property is fully exempt from LTCG if held for 2+ years. Must have been your residence. Indexation doesn't apply here.",
        },
        {
          question: "What's the difference between equity and property LTCG?",
          answer:
            "Equity LTCG: 20% tax without indexation (from April 2024). Property LTCG: 20% tax with indexation. Property treatment is much better due to indexation.",
        },
        {
          question: "Is cost of acquisition deductible from gain?",
          answer:
            "Yes, brokerage, registration, legal fees are deductible as cost of acquisition. This reduces taxable gain. Keep all receipts.",
        },
        {
          question: "How long must I hold an asset for LTCG?",
          answer:
            "Equity/MF: 1 year. Property: 2 years. Gold: 3 years. Virtual assets: 2 years. Holding period is calculated from date of registration/purchase (not sale).",
        }
      ],
      relatedSlugs: [
        "tax/stcg-tax-calculator",
        "tax/capital-gains-indexation",
        "property/capital-gains-property",
      ],
    },
    }),

  // Remaining tools (16-30) - brief implementations
  // 16. STCG Tax Calculator
  createPercentageTool({
    slug: "stcg-tax-calculator",
    category: "tax",
    title: "Short-Term Capital Gains Tax Calculator",
    shortTitle: "STCG Tax",
    description: "Calculate tax on short-term capital gains from quick asset sales",
    keywords: ["STCG", "short term capital gains"],
    amountLabel: "Capital Gain Amount",
    rateLabel: "Your Tax Slab Rate",
    resultLabel: "STCG Tax Liability",
    defaultAmount: 500000,
    maxAmount: 50000000,
    defaultRate: 30,
    maxRate: 30,
    whatIs:
      "Short-Term Capital Gains (STCG) result from selling investment assets held for less than specified periods (typically under 1-3 years depending on asset type) at profit. Unlike long-term gains with favorable tax rates, short-term gains are taxed as normal income at your slab rate (up to 30%), making short-term trading significantly more expensive from tax perspective. For intraday stock trading and equity derivative trades, STCG attracts 15% tax regardless of income slab, incentivizing active traders. This distinction between holding periods is crucial because a stock held 11 months attracts 30% tax while holding 13 months attracts only 20% tax, potentially saving thousands in taxes. This calculator evaluates your short-term trading results and shows tax impact, essential for traders planning exit strategies and evaluating whether short-term profits justify tax cost. Many traders underestimate STCG tax burden, surprised by 30% tax on profits they believed were untaxed. Understanding STCG implications helps traders decide whether to hold longer for LTCG benefits or execute trades based on market timing versus tax considerations. The Finance Act 2024 restructured STCG provisions, requiring updated calculations for recent transactions.",
      sections: [],
    faqs: [
      {
        question: "Why is STCG taxed at slab rate?",
        answer:
          "STCG is added to your income and taxed at applicable slab (0%, 5%, 20%, 30%). This encourages long-term investing and discourages short-term speculation.",
      },
      {
        question: "How much can I save by holding longer?",
        answer:
          "For equity, holding 1+ year reduces tax from 30% (slab) to 20% (LTCG). Saving: ₹10 per ₹100 gain. For property, holding 2+ years gets indexation benefit (additional 20-30% saving).",
      },
      {
        question: "Can I adjust losses against gains?",
        answer:
          "Yes, capital losses can be adjusted against capital gains to reduce taxable gain. Unused losses can be carried forward for 8 years.",
        },
      {
        question: "What counts as holding period start date?",
        answer:
          "For equity: Date of purchase. For property: Date of registration/completion. For gift: Date of gift reception. Always clarify with tax advisor for edge cases.",
        }
    ],
    relatedSlugs: [
      "tax/ltcg-tax-calculator",
      "tax/capital-gains-indexation",
      "investment/equity-investment",
    ],
  }),

  // 17. Capital Gains Indexation
  createPercentageTool({
    slug: "capital-gains-indexation",
    category: "tax",
    title: "Capital Gains Indexation Calculator",
    shortTitle: "Indexation Benefit",
    description: "Reduce capital gains tax using indexation benefit on held assets",
    keywords: ["indexation", "inflation adjustment", "cost basis"],
    amountLabel: "Original Cost of Asset",
    rateLabel: "Average Annual Inflation",
    resultLabel: "Indexed Cost",
    defaultAmount: 1000000,
    maxAmount: 50000000,
    defaultRate: 5,
    maxRate: 8,
    whatIs:
      "Indexation is a powerful tax benefit allowing individuals to reduce capital gains tax by adjusting purchase price for inflation using Cost Inflation Index (CII) provided by the Income Tax Department. While calculating long-term capital gains on real estate, gold, and bonds, you can increase the cost basis using the CII ratio between purchase year and sale year, significantly reducing taxable gains. For example, a property purchased for 10 lakhs rupees in 2010 and sold for 30 lakhs rupees in 2024 shows 20 lakh rupees gain nominally, but indexation might increase cost basis to 25 lakhs rupees, reducing taxable gain to just 5 lakhs rupees (saving 100000 rupees in taxes at 20% rate). This calculator integrates CII data and automatically computes indexed cost, determining exact LTCG liability after inflation adjustment. The indexation benefit is particularly valuable for property sales and long-held gold investments, which typically appreciate substantially with time. Annual CII tables are published by the Income Tax Department, making this calculator essential for accurate LTCG reporting and ITR filing. Many property sellers miss indexation benefits or calculate them incorrectly, paying excess taxes unnecessarily.",
      sections: [],
    faqs: [
      {
        question: "What inflation rate should I use?",
        answer:
          "Use official indexation factor released by income tax department annually. Average is 4-6%. For rough estimates, use 5-6%.",
      },
      {
        question: "Does indexation apply to all assets?",
        answer:
          "Indexation is available for property, gold, mutual funds, debentures held 2+ years. Not available for equity (from April 2024). Rules vary by asset type.",
      },
      {
        question: "How much can indexation reduce my tax?",
        answer:
          "For a property held 10 years at 5% inflation, indexed cost becomes 163% of original, potentially reducing taxable gain by 30-40% depending on actual appreciation.",
        },
    ],
    relatedSlugs: [
      "tax/ltcg-tax-calculator",
      "property/capital-gains-property",
      "investment/investment-tax",
    ],
  }),

  // 18-30: Professional tax, advance calculations, deduction calculators
  // Remaining tools condensed...

  // 18. Professional Tax
  createPercentageTool({
    slug: "professional-tax-calculator",
    category: "tax",
    title: "Professional Tax Calculator",
    shortTitle: "Professional Tax",
    description: "Calculate professional tax applicable in your state",
    keywords: ["professional tax", "state tax", "income tax"],
    amountLabel: "Annual Income",
    rateLabel: "Professional Tax Rate",
    resultLabel: "Professional Tax",
    defaultAmount: 1500000,
    maxAmount: 50000000,
    defaultRate: 0.5,
    maxRate: 2,
    whatIs:
      "Professional Tax is a state-level tax imposed on salaried employees and professionals in certain Indian states, primarily Maharashtra, Karnataka, Tamil Nadu, and others. This is separate from income tax and GST, adding to overall tax burden for individuals in these states. Professional tax rates vary by state and income slab, ranging from 0-2500 rupees per annum in most states, with Karnataka and Maharashtra having slightly higher brackets. This tax is typically deducted at source by employers monthly, similar to TDS on salary, and credited to state revenue accounts. While professional tax appears small (often under 200 rupees monthly), understanding it helps employees verify payroll deductions and plan take-home compensation accurately. For self-employed professionals and business owners, professional tax becomes due when income exceeds state-specific thresholds. This calculator determines your exact professional tax liability based on state residence and income level, helping you verify employer calculations and estimate net salary accurately.",
      sections: [],
    faqs: [
      {
        question: "Which states levy professional tax?",
        answer:
          "Maharashtra, Karnataka, Tamil Nadu, Andhra Pradesh, Telangana, and a few others. Many states have abolished it. Check your state's revenue department for applicability.",
      },
      {
        question: "Is professional tax deductible?",
        answer:
          "In some states (Maharashtra), it's deductible from income. In others, it's a separate tax liability. Always verify your state's rules.",
      },
      {
        question: "Who is liable to pay professional tax?",
        answer:
          "Individuals earning from profession, business, employment, or rental income above a threshold (varies by state). Salaried employees in some states are also liable.",
      }
    ],
    relatedSlugs: [
      "tax/income-tax-calculator",
      "business/business-tax",
      "tax/section-80c-calculator",
    ],
  }),

  // 19. Surcharge Calculator
  createPercentageTool({
    slug: "surcharge-calculator",
    category: "tax",
    title: "Income Tax Surcharge Calculator",
    shortTitle: "Surcharge",
    description: "Calculate surcharge on high incomes above ₹50 lakhs",
    keywords: ["surcharge", "high income tax", "additional tax"],
    amountLabel: "Total Income",
    rateLabel: "Surcharge Rate (if applicable)",
    resultLabel: "Surcharge Amount",
    defaultAmount: 50000000,
    maxAmount: 500000000,
    defaultRate: 15,
    maxRate: 25,
    whatIs:
      "Surcharge is an additional tax levied on individuals with very high income above specified thresholds (50 lakhs, 1 crore, and 2 crore under Finance Act 2024), on top of the regular income tax already calculated. Surcharge rates escalate with income: 10% for income 50 lakhs-1 crore, 15% for 1 crore-2 crore, and 25% for above 2 crore. This progressive surcharge ensures the ultra-high income earners contribute proportionally higher taxes, implementing equity in taxation. Surcharge applies to total income before allowing special deductions but includes all income sources: salary, business, capital gains, interest, and rental income. For high-income individuals, surcharge can add 5-30 lakhs rupees to annual tax liability, making accurate calculation and advance tax planning essential. This calculator helps high-net-worth individuals understand total tax liability including surcharge, enabling proper cash flow planning and advance tax scheduling.",
      sections: [],
    faqs: [
      {
        question: "At what income does surcharge apply?",
        answer:
          "Surcharge applies at ₹50L (10%), ₹1Cr (15%), ₹2Cr (25%), ₹5Cr (37%). Each slab has its own surcharge rate.",
      },
      {
        question: "Can I reduce surcharge through deductions?",
        answer:
          "Surcharge is calculated on total income after deductions, so yes, deductions reduce surcharge indirectly. However, reducing income to below surcharge threshold is difficult.",
      }
    ],
    relatedSlugs: [
      "tax/income-tax-calculator",
      "tax/income-tax-old-regime",
      "tax/hra-exemption-calculator",
    ],
  }),

  // 20. Education Cess
  createPercentageTool({
    slug: "education-cess-calculator",
    category: "tax",
    title: "Health & Education Cess Calculator",
    shortTitle: "Health & Education Cess",
    description: "Understand Education Cess impact on your total tax liability",
    keywords: ["cess", "education cess", "health cess"],
    amountLabel: "Income Tax Amount",
    rateLabel: "Cess Rate (4%)",
    resultLabel: "Cess Amount",
    defaultAmount: 500000,
    maxAmount: 10000000,
    defaultRate: 4,
    maxRate: 4,
    whatIs:
      "Health and Education Cess is a surcharge on income tax at 4% of the computed income tax amount itself, not on income. Originally introduced at 2%, it was increased to 4% from FY 2018-19 onwards. This cess applies uniformly to all taxpayers regardless of income level and is mandatory with no exemptions or deductions. The cess is added after calculating regular income tax but before considering any surcharge for high incomes. While 4% appears small, for a 5 lakh rupee income tax liability, the cess amounts to 20000 rupees, becoming significant for high earners. This calculator shows the exact cess amount you'll pay annually, helping understand total tax burden beyond base income tax rates. Understanding cess is important for accurate ITR filing, advance tax planning, and total cost-of-living calculations for individuals and families.",
      sections: [],
    faqs: [
      {
        question: "Is cess deductible?",
        answer:
          "No, cess is a separate charge on income tax and surcharge. Not deductible or reducible through any means.",
      }
    ],
    relatedSlugs: [
      "tax/income-tax-calculator",
      "tax/surcharge-calculator",
      "tax/advance-tax-calculator",
    ],
  }),

  // 21. Tax Saving Calculator
  createPercentageTool({
    slug: "tax-saving-calculator",
    category: "tax",
    title: "Tax Saving Investment Planning Calculator",
    shortTitle: "Tax Saving Plan",
    description: "Identify best strategies to reduce your overall tax burden",
    keywords: ["tax saving", "investment planning", "deduction"],
    inputs: [
      {
        type: "number",
        key: "taxableIncome",
        label: "Expected Taxable Income",
        prefix: "₹",
        min: 0,
        max: 50000000,
        step: 100000,
        default: 1500000,
      },
      {
        type: "number",
        key: "targetIncome",
        label: "Target Taxable Income (after deduction)",
        prefix: "₹",
        min: 0,
        max: 50000000,
        step: 100000,
        default: 1350000,
      },
      {
        type: "number",
        key: "taxSlab",
        label: "Your Income Tax Slab Rate",
        suffix: "%",
        min: 0,
        max: 45,
        step: 5,
        default: 20,
      }
    ],
    calculate(v) {
      const currentIncome = v.taxableIncome as number;
      const targetIncome = v.targetIncome as number;
      const slab = v.taxSlab as number;

      const investmentNeeded = currentIncome - targetIncome;
      const taxSaved = (investmentNeeded * slab) / 100;

      return {
        investmentNeeded: Math.round(investmentNeeded),
        taxSaved: Math.round(taxSaved),
        currentTaxableIncome: currentIncome,
        targetTaxableIncome: targetIncome,
        effectiveSaving: Math.round(taxSaved),
      };
    },
    resultFields: [
      { key: "investmentNeeded", label: "Investment Needed", format: "currency", isPrimary: true },
      { key: "taxSaved", label: "Tax Saved", format: "currency" },
    ],
    content: {
      whatIs:
        "A comprehensive tax-saving strategy considers all available deductions, exemptions, and investment opportunities across the financial year. This calculator helps individuals identify tax-saving opportunities by analyzing income sources, existing investments, and applicable deductions. Key opportunities include maximizing Section 80C investments (150000 rupee limit across PPF, ELSS, LIC), claiming Section 80D health insurance (150000-250000 rupees total), utilizing Section 80E education loan interest (unlimited), and leveraging HRA exemptions for salaried employees. The calculator compares old versus new regime choice, identifies missed deduction opportunities, and suggests optimal investment allocation. For business owners, it considers business losses, depreciation, and professional expenses. Successful tax planning isn't just about paying minimum tax but aligning tax strategy with financial goals. This calculator helps identify personalized savings opportunities, whether through early retirement savings in PPF or strategic health insurance investment.",
      sections: [],
      faqs: [
        {
          question: "How do I use this for planning?",
          answer:
            "If your income is likely ₹15L and you want ₹13.5L taxable (save 1.5L in deductions), and you're in 20% slab, you save ₹30K tax. Invest in PPF/ELSS accordingly.",
        }
      ],
      relatedSlugs: [
        "tax/section-80c-calculator",
        "tax/section-80d-calculator",
        "tax/income-tax-calculator",
      ],
    },
    }),

  // Remaining simplified tools...
  // 22-30: More tax calculators
  createPercentageTool({
    slug: "tax-on-fd-interest",
    category: "tax",
    title: "Tax on FD Interest Calculator",
    shortTitle: "FD Interest Tax",
    description: "Calculate tax on fixed deposit interest income and TDS implications",
    keywords: ["FD tax", "interest tax", "bank interest"],
    amountLabel: "Annual FD Interest",
    rateLabel: "Your Income Tax Slab",
    resultLabel: "Tax on Interest",
    defaultAmount: 100000,
    maxAmount: 5000000,
    defaultRate: 20,
    maxRate: 30,
    whatIs:
      "Interest earned on fixed deposits (FDs) is taxable income under Section 56 of the Income Tax Act 1961, added to your gross income and taxed at applicable slab rates. Banks deduct TDS (Tax Deducted at Source) on FD interest automatically when annual interest exceeds 40000 rupees (50000 for senior citizens), providing advance tax payment. This calculator determines exact tax liability on FD interest considering your income slab and calculates TDS deduction, helping understand net interest received. For senior citizens, investing in FDs provides regular income stream with favorable TDS thresholds and tax benefits. Many investors underestimate tax on FD returns, expecting full interest withdrawal but receiving reduced amount after TDS. Understanding tax-adjusted FD returns helps compare with tax-free investments (PPF, Sukanya Samriddhi) and plan optimal allocation.",
      sections: [],
    faqs: [
      {
        question: "Is FD interest taxable?",
        answer:
          "Yes, all FD interest is taxable income. Even if TDS isn't deducted (interest <₹40K), you must report it in ITR.",
      },
      {
        question: "Is there any exemption on FD interest?",
        answer:
          "No exemption, but senior citizens (60+) get ₹50K exemption on interest from govt securities and bank deposits. Non-residents can get treaty benefits.",
      }
    ],
    relatedSlugs: [
      "tax/income-tax-calculator",
      "tax/tds-calculator",
      "investment/fixed-deposit",
    ],
  }),

  createPercentageTool({
    slug: "tax-on-mutual-fund",
    category: "tax",
    title: "Mutual Fund Redemption Tax Calculator",
    shortTitle: "MF Tax",
    description: "Calculate tax on mutual fund income from dividends and capital gains",
    keywords: ["mutual fund tax", "LTCG", "MF redemption"],
    amountLabel: "Redemption Amount",
    rateLabel: "Investment Cost (for gain %)",
    resultLabel: "Estimated Gain",
    defaultAmount: 500000,
    maxAmount: 50000000,
    defaultRate: 400000,
    maxRate: 50000000,
    whatIs:
      "Mutual fund investments provide income through two routes: dividend payouts and capital appreciation on redemption. Dividend income was recently made taxable (previously dividend distribution tax was deducted at source), adding to overall tax burden. Capital gains from mutual funds are classified as long-term (held 1+ years, 20% tax with indexation benefit) or short-term (under 1 year, 15% tax for equity funds, slab rate for debt). This calculator helps investors understand net returns after tax, essential for comparing different fund categories and investment horizons. Many investors are surprised by tax liability on mutual fund profits, expecting better returns than actual net amount received. Understanding tax-adjusted returns influences fund selection, holding period decisions, and strategic redemptions during financial year.",
      sections: [],
    faqs: [
      {
        question: "How long must I hold MF for LTCG?",
        answer:
          "Equity MF: 1 year. Debt/Hybrid MF: 2 years. Holding period is from purchase date to redemption date. Redeeming 1 year and 1 day qualifies for LTCG.",
      },
      {
        question: "Is dividend received from MF taxable?",
        answer:
          "From 2023, dividend from MF is taxed as capital gain in the hands of the investor. TDS may be deducted at source.",
      }
    ],
    relatedSlugs: [
      "tax/ltcg-tax-calculator",
      "tax/stcg-tax-calculator",
      "investment/mutual-fund",
    ],
  }),

  // Remaining placeholders...
  createPercentageTool({
    slug: "custom-duty-calculator",
    category: "tax",
    title: "Custom Duty Calculator",
    shortTitle: "Custom Duty",
    description: "Calculate import duties on items brought into India",
    keywords: ["custom duty", "import duty", "tariff"],
    amountLabel: "CIF Value (Cost + Insurance + Freight)",
    rateLabel: "Applicable Duty Rate",
    resultLabel: "Custom Duty Amount",
    defaultAmount: 500000,
    maxAmount: 50000000,
    defaultRate: 10,
    maxRate: 100,
    whatIs:
      "Custom Duty is levied on goods imported into India, adding to product cost and managed by Indian Customs authorities under Customs Act 1962. Duty rates vary by product category (5-40% typically) and country of origin, with special rates for SAARC and other trade agreement countries. Personal baggage and gifts have specific customs exemptions (20000-50000 rupees depending on status), making understanding duty essential for travelers. This calculator helps travelers and importers calculate duty payable on purchased goods and evaluate whether buying domestically is cheaper. India's Make in India initiative involves duty structures protecting domestic industries, making imported items expensive.",
      sections: [],
    faqs: [
      {
        question: "How is CIF value determined?",
        answer:
          "CIF = Cost of goods + Insurance + Freight to Indian port. Invoice price is used as cost. This is the base for custom duty calculation.",
      }
    ],
    relatedSlugs: [
      "tax/gst-calculator",
      "business/import-export",
    ],
  }),

  createPercentageTool({
    slug: "road-tax-calculator",
    category: "tax",
    title: "Road Tax / Vehicle Tax Calculator",
    shortTitle: "Road Tax",
    description: "Calculate road tax and vehicle registration fees for your vehicle",
    keywords: ["road tax", "vehicle tax", "registration tax"],
    amountLabel: "Vehicle Value",
    rateLabel: "Road Tax Rate (State Specific)",
    resultLabel: "Road Tax Amount",
    defaultAmount: 1000000,
    maxAmount: 50000000,
    defaultRate: 4,
    maxRate: 8,
    whatIs:
      "Road Tax (also called vehicle tax) is a state-level tax levied on vehicle ownership, varying significantly by state and vehicle type. Four-wheeler road tax ranges from 3-15% of vehicle value depending on state and vehicle age, affecting total cost of vehicle ownership. This tax is mandatory for vehicle registration in India and must be paid upfront during registration, with rates increasing on renewal. Understanding road tax helps in vehicle purchase decisions and budgeting total ownership cost beyond purchase price and insurance. Road tax structure encourages purchase of lower-value vehicles and makes older vehicles expensive to maintain, influencing vehicle lifecycle economics.",
      sections: [],
    faqs: [
      {
        question: "When is road tax paid?",
        answer:
          "During vehicle registration at RTO. Later, renewed annually/biannually based on state rules and vehicle age.",
      }
    ],
    relatedSlugs: [
      "vehicle/vehicle-loan-emi",
      "tax/income-tax-calculator",
    ],
  }),

  createPercentageTool({
    slug: "agricultural-income-tax",
    category: "tax",
    title: "Agricultural Income Tax Calculator",
    shortTitle: "Ag. Income Tax",
    description: "Calculate tax on agricultural income exemptions and thresholds",
    keywords: ["agricultural income", "farm income", "farming tax"],
    amountLabel: "Agricultural Income",
    rateLabel: "Tax Rate (if applicable)",
    resultLabel: "Tax Amount",
    defaultAmount: 500000,
    maxAmount: 50000000,
    defaultRate: 0,
    maxRate: 30,
    whatIs:
      "Agricultural income earned by farmers and landowners from farming operations is exempt from income tax under Section 10(1) of the Income Tax Act 1961, making India's tax system favorable to farming community. However, income from selling agricultural land, processing farm produce, or non-farming activities is taxable. This exemption applies regardless of income amount, meaning even crore-rupee earning farmers pay no income tax if income is purely agricultural. However, such farmers must file ITR to claim various exemptions and deductions on non-agricultural income (interest, rent, capital gains). The calculator helps farmers understand income tax implications of their activities, ensuring they don't inadvertently convert exempt agricultural income into taxable business income through processing or trading.",
      sections: [],
    faqs: [
      {
        question: "What is considered agricultural income?",
        answer:
          "Income from agriculture (farming, growing crops) on land in India is exempt. Income from selling agricultural land or machinery is not agricultural income.",
      }
    ],
    relatedSlugs: [
      "tax/income-tax-calculator",
      "business/business-tax",
    ],
  }),

  createPercentageTool({
    slug: "tds-on-property-nri",
    category: "tax",
    title: "TDS on Property Sale by NRI",
    shortTitle: "NRI Property TDS",
    description: "Calculate TDS on property sales for Non-Resident Indian transactions",
    keywords: ["NRI", "property sale", "TDS"],
    amountLabel: "Property Sale Value",
    rateLabel: "TDS Rate (30% for NRI)",
    resultLabel: "TDS Amount",
    defaultAmount: 5000000,
    maxAmount: 100000000,
    defaultRate: 30,
    maxRate: 30,
    whatIs:
      "Section 194LA imposes mandatory TDS at 20% on property sale consideration when buyers transfer property in India. Additionally, Section 194O imposes TDS at 10% on rent paid to Non-Resident Indians (NRIs) for property transactions. When NRIs sell Indian property, tax implications are complex involving capital gains tax, remittance considerations, and TDS requirements. This calculator helps NRIs understand tax liability on property sales and buyers understand TDS obligations. For NRIs managing property in India, understanding these tax implications prevents compliance issues and ensures proper ITR filing, which NRIs must do even if they're not taxable residents.",
      sections: [],
    faqs: [
      {
        question: "Can NRI claim lower TDS?",
        answer:
          "Yes, if NRI is eligible for LTCG treatment (2 years+ holding), they file ITR to settle actual tax. Otherwise, 30% TDS is final tax.",
      }
    ],
    relatedSlugs: [
      "property/capital-gains-property",
      "tax/ltcg-tax-calculator",
      "nri/nri-tax",
    ],
  }),

  createPercentageTool({
    slug: "section-80g-calculator",
    category: "tax",
    title: "Section 80G Donation Deduction Calculator",
    shortTitle: "80G Donations",
    description: "Calculate tax deduction benefits from charitable donations",
    keywords: ["80G", "charity", "donation deduction"],
    amountLabel: "Donation Amount",
    rateLabel: "Deduction Percentage (50% or 100%)",
    resultLabel: "80G Deduction",
    defaultAmount: 100000,
    maxAmount: 5000000,
    defaultRate: 50,
    maxRate: 100,
    whatIs:
      "Section 80G of the Income Tax Act 1961 allows deduction of charitable donations made to eligible organizations, providing 50% or 100% deduction depending on organization type. Political donations and donations to Prime Minister's Relief Fund allow 100% deduction, while general charitable contributions allow 50% deduction. The deduction is available both under old and new regimes, making it valuable for philanthropists. This calculator identifies donation deductibility, calculates tax savings, and helps donors plan charitable giving strategically. India's tax policy incentivizes charitable contributions through this Section, encouraging wealthy individuals to support social causes while reducing personal tax liability. For donors managing budgets, understanding tax-adjusted donation cost helps allocate resources efficiently.",
      sections: [],
    faqs: [
      {
        question: "Which charities qualify for 80G?",
        answer:
          "Only FCRA-registered or 80G-approved charities. Check income tax website for approved list. Donations to non-approved charities don't get deduction.",
      }
    ],
    relatedSlugs: [
      "tax/section-80c-calculator",
      "tax/income-tax-old-regime",
    ],
  }),

  createPercentageTool({
    slug: "section-80e-calculator",
    category: "tax",
    title: "Section 80E Education Loan Interest Deduction",
    shortTitle: "80E",
    description: "Calculate tax deduction on education loan interest payments",
    keywords: ["80E", "education loan", "interest deduction"],
    amountLabel: "Education Loan Interest Paid",
    rateLabel: "Tax Slab Rate",
    resultLabel: "Tax Saving from 80E",
    defaultAmount: 100000,
    maxAmount: 5000000,
    defaultRate: 20,
    maxRate: 30,
    whatIs:
      "Section 80E provides unlimited deduction for interest paid on education loans taken for higher education (undergraduate, postgraduate, professional courses). This unlimited deduction (no 150000 rupee cap like Section 80C) applies during the loan repayment period and for 8 years maximum after the course ends. The deduction applies to interest on loans borrowed for your own education, spouse's education, or dependent child's education. With rising higher education costs, Section 80E becomes increasingly valuable, often saving 30000-100000+ rupees annually for borrowers in early repayment phase. This calculator determines deduction eligibility and tax savings, helping education loan borrowers understand financial benefit. For students planning education financing, understanding Section 80E makes education loan more affordable than initially calculated.",
      sections: [],
    faqs: [
      {
        question: "How long can I claim 80E deduction?",
        answer:
          "Up to 8 years from the year repayment starts. If you start repaying in year 3 after loan, you can claim 8 years from year 3.",
      }
    ],
    relatedSlugs: [
      "tax/section-80c-calculator",
      "tax/income-tax-old-regime",
      "education/education-loan-emi",
    ],
  }),

  createPercentageTool({
    slug: "presumptive-tax-calculator",
    category: "tax",
    title: "Presumptive Tax Calculator (44AD/44ADA)",
    shortTitle: "Presumptive Tax",
    description: "Calculate presumptive tax for small business and trading operations",
    keywords: ["44AD", "44ADA", "presumptive income", "business tax"],
    amountLabel: "Gross Turnover/Receipts",
    rateLabel: "Presumptive Rate (8% for 44AD, 50% for 44ADA)",
    resultLabel: "Presumptive Income",
    defaultAmount: 5000000,
    maxAmount: 500000000,
    defaultRate: 8,
    maxRate: 50,
    whatIs:
      "Section 44AD (for professionals) and 44ADA (for traders) allow self-employed individuals with gross receipts below specified thresholds to compute income as 8% of receipts for professionals or 6% of receipts for traders, instead of showing actual business profit/loss. This presumptive taxation simplifies compliance for small businesses, eliminating need for maintaining detailed accounts and reduces audit risk. The simplified return filing through this method allows businesses to claim estimated 8-6% profit regardless of actual profitability, benefiting businesses with genuine lower margins. This calculator helps small business owners determine if presumptive taxation is beneficial, comparing it with normal profit computation methods. Many small traders find presumptive taxation attractive for simplicity despite potentially paying slightly higher tax, valuing reduced compliance burden.",
      sections: [],
    faqs: [
      {
        question: "Who can use 44AD?",
        answer:
          "Individuals/HUF with business turnover <₹2 crore. Can assume 8% profit automatically. No detailed P&L needed.",
      },
      {
        question: "Can I claim lower profit if actual is lower?",
        answer:
          "No, presumptive schemes require minimum profit assumption. If you claim lower, you lose the benefit.",
      }
    ],
    relatedSlugs: [
      "business/business-tax",
      "tax/income-tax-calculator",
      "tax/income-tax-old-regime",
    ],
  }),
];
